"use client"

import { HttpTypes } from "@medusajs/types"
import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import Image from "next/image"
import { normalizeImageUrl } from "@lib/util/image-url"

type ProductImageCarouselProps = {
  images: HttpTypes.StoreProductImage[]
  productTitle: string
}

const ProductImageCarousel = ({ images, productTitle }: ProductImageCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const currentImageUrl = normalizeImageUrl(images[currentIndex]?.url)

  if (!images || images.length === 0) {
    return (
      <div className=" relative bg-beige-light rounded-lg overflow-hidden h-96 lg:h-[500px] flex items-center justify-center">
        <p className="text-brown-dark/50">No images available</p>
      </div>
    )
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length)
  }

  useEffect(() => {
    if (!isLightboxOpen) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsLightboxOpen(false)
        return
      }

      if (event.key === "ArrowLeft") {
        goToPrevious()
      }

      if (event.key === "ArrowRight") {
        goToNext()
      }
    }

    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      document.body.style.overflow = originalOverflow
    }
  }, [isLightboxOpen, images.length])

  return (
    <div className="flex flex-col gap-4">
      {/* Main Image Display */}
      <div className=" relative bg-beige-light rounded-lg overflow-hidden h-96 lg:h-[500px] group">
        {currentImageUrl && (
          <button
            type="button"
            onClick={() => setIsLightboxOpen(true)}
            className="w-full h-full cursor-zoom-in"
            aria-label="Open image viewer"
          >
            <Image
              src={currentImageUrl}
              alt={`${productTitle} - Image ${currentIndex + 1}`}
              fill
              priority={currentIndex === 0}
              sizes="(max-width: 1024px) 100vw, 50vw"
              className="object-cover"
            />
          </button>
        )}

        {/* Navigation Arrows - Only show if more than 1 image */}
        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-brown-dark/60 hover:bg-brown-dark/80 text-cream rounded-full p-3 transition-all opacity-0 group-hover:opacity-100 z-20"
              aria-label="Previous image"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              type="button"
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-brown-dark/60 hover:bg-brown-dark/80 text-cream rounded-full p-3 transition-all opacity-0 group-hover:opacity-100 z-20"
              aria-label="Next image"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Image Counter */}
            <div className="absolute top-4 right-4 bg-brown-dark/60 text-cream px-3 py-1 rounded-full text-sm z-20">
              {currentIndex + 1} / {images.length}
            </div>
          </>
        )}
      </div>

      {/* Thumbnail Navigation - Only show if more than 1 image */}
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
          {images.map((image, index) => {
            const imageUrl = normalizeImageUrl(image.url)

            return (
              <button
                key={image.id || index}
                type="button"
                onClick={() => setCurrentIndex(index)}
                className={`flex-shrink-0 relative w-20 h-20 rounded-md overflow-hidden border-2 transition-all ${index === currentIndex
                  ? "border-brown-dark ring-2 ring-brown-dark/30"
                  : "border-beige-light hover:border-brown-dark/40"
                  }`}
                aria-label={`View image ${index + 1}`}
              >
                {imageUrl && (
                  <Image
                    src={imageUrl}
                    alt={`${productTitle} - Thumbnail ${index + 1}`}
                    width={80}
                    height={80}
                    sizes="80px"
                    loading="lazy"
                    className="w-full h-full object-contain bg-beige-light"
                  />
                )}
              </button>
            )
          })}
        </div>
      )}

      {isLightboxOpen && currentImageUrl && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 backdrop-blur-xl p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`${productTitle} image viewer`}
          onClick={() => setIsLightboxOpen(false)}
        >
          {/* Close Button */}
          <button
            type="button"
            className="absolute top-4 right-4 md:top-8 md:right-8 text-cream/60 hover:text-cream hover:rotate-90 transition-all duration-300 z-[110]"
            onClick={() => setIsLightboxOpen(false)}
            aria-label="Close image viewer"
          >
            <svg className="w-8 h-8 md:w-10 md:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div
            className="relative w-full h-full max-w-[95vw] max-h-[95vh] flex flex-col justify-center items-center"
            onClick={(event) => event.stopPropagation()}
          >
            {/* Main Image Container */}
            <div className="relative flex-1 w-full min-h-0 flex items-center justify-center">
              <img
                src={currentImageUrl}
                alt={`${productTitle} - Image ${currentIndex + 1}`}
                className="max-w-full max-h-full object-contain drop-shadow-2xl"
              />

              {images.length > 1 && (
                <>
                  {/* Nav Buttons */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                    className="absolute left-0 top-1/2 -translate-y-1/2 text-cream/40 hover:text-cream hover:scale-110 transition-all p-4"
                    aria-label="Previous image"
                  >
                    <svg className="w-10 h-10 md:w-16 md:h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); goToNext(); }}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-cream/40 hover:text-cream hover:scale-110 transition-all p-4"
                    aria-label="Next image"
                  >
                    <svg className="w-10 h-10 md:w-16 md:h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
            </div>

            {/* Thumbnails Strip */}
            {images.length > 1 && (
              <div className="h-20 md:h-24 mt-4 shrink-0 flex gap-3 overflow-x-auto pb-2 no-scrollbar px-4 items-center justify-center w-full">
                {images.map((image, index) => {
                  const imageUrl = normalizeImageUrl(image.url)

                  return (
                    <button
                      key={image.id || index}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
                      className={`flex-shrink-0 relative w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden transition-all duration-300 ${index === currentIndex
                        ? "ring-2 ring-gold scale-105 opacity-100"
                        : "opacity-40 hover:opacity-100 hover:scale-105"
                        }`}
                      aria-label={`View image ${index + 1}`}
                    >
                      {imageUrl && (
                        <img
                          src={imageUrl}
                          alt={`${productTitle} - Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </button>
                  )
                })}
              </div>
            )}

            <div className="absolute top-0 left-4 md:left-0 text-cream/40 text-sm tracking-widest font-serif">
              {currentIndex + 1} / {images.length}
            </div>

          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default ProductImageCarousel
